import { Spinner } from '@optools/components/loading/spinner';
import { Button } from '@optools/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@optools/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@optools/components/ui/popover';
import { cn } from '@optools/components/ui/utils';
import { Check, ChevronDownIcon } from 'lucide-react';
import * as React from 'react';
import { useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';

export interface ComboboxData {
  label: string;
  value: string;
}

export interface WindowedComboboxProps {
  alwaysShowPlaceholder?: boolean;
  ['aria-label']?: string;
  data: ComboboxData[];
  disabled?: boolean;
  /** A callback function that runs on each option to determine whether the option should be disabled */
  disableOption?: (item: ComboboxData) => boolean;
  fitContainer?: boolean;
  loading?: boolean;
  onValueChange: (value: string) => void;
  placeHolder: string;
  value?: string | string[];
}

export function WindowedCombobox({
  alwaysShowPlaceholder = false,
  data,
  disabled = false,
  disableOption,
  fitContainer = false,

  loading = false,
  onValueChange,
  placeHolder,
  value,
  ...props
}: WindowedComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState(new RegExp(''));

  const filteredData = useMemo(() => {
    return data.filter((item) => searchTerm.test(item.label) || searchTerm.test(item.value));
  }, [data, searchTerm]);

  const handleFilter = (value: string) => {
    setSearchTerm(new RegExp(value, 'i'));
  };

  const onOpenChangeHandler = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) setSearchTerm(new RegExp(''));
  };

  const doesItemMatchValue = (item: string) => {
    if (!Array.isArray(value)) {
      return item === value;
    }

    return !!~value.indexOf(item);
  };

  return (
    <Popover modal={true} open={open} onOpenChange={onOpenChangeHandler}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          className='relative w-full max-w-full justify-between overflow-hidden [&_svg:not([class*="text-"])]:text-muted-foreground'
          disabled={disabled}
          role="combobox"
          variant="outline"
          {...props}>
          <span className={cn({ 'absolute left-2 max-w-[calc(95%-16px)] truncate': fitContainer })}>
            {!alwaysShowPlaceholder && value && value.length > 0
              ? data.find((item) => doesItemMatchValue(item.value))?.label
              : placeHolder}
          </span>
          {loading ? (
            <Spinner
              className={cn('ml-2 size-4 shrink-0', {
                'absolute right-2': fitContainer,
              })}
            />
          ) : (
            <ChevronDownIcon
              className={cn('ml-2 size-4 shrink-0', {
                'absolute right-2': fitContainer,
              })}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-full min-w-(--radix-popper-anchor-width) p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder={placeHolder} onValueChange={handleFilter} />
          <CommandList>
            <CommandEmpty>No Values Found.</CommandEmpty>
            <CommandGroup>
              <List
                height={filteredData.length < 5 ? filteredData.length * 40 : 300}
                itemCount={filteredData.length}
                itemSize={40}
                width="100%">
                {({ index, style }) => {
                  const item = filteredData[index];
                  return (
                    <CommandItem
                      key={item.value}
                      className="cursor-pointer"
                      disabled={disableOption?.(item)}
                      style={style}
                      value={item.value}
                      onSelect={(currentValue) => {
                        onValueChange(currentValue);
                        setOpen(false);
                      }}>
                      <Check
                        className={cn(
                          'size-4 shrink-0',
                          doesItemMatchValue(item.value) ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <span className="overflow-auto">{item.label}</span>
                    </CommandItem>
                  );
                }}
              </List>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
